import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Shield, Scale } from 'lucide-react';

export default function Terms() {
  return (
    <div className="space-y-6" data-testid="terms-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight">Terms & Agreements</h1>
        <p className="text-slate-600 mt-1">Legal information for using BSO SiteTrack</p>
      </div>

      <Tabs defaultValue="terms" className="w-full">
        <TabsList className="grid w-full grid-cols-3 rounded-sm h-auto">
          <TabsTrigger value="terms" className="rounded-sm data-[state=active]:bg-[#1A3668] data-[state=active]:text-white py-3">
            <FileText className="w-4 h-4 mr-2" />
            Terms of Service
          </TabsTrigger>
          <TabsTrigger value="privacy" className="rounded-sm data-[state=active]:bg-[#1A3668] data-[state=active]:text-white py-3">
            <Shield className="w-4 h-4 mr-2" />
            Privacy Policy
          </TabsTrigger>
          <TabsTrigger value="acceptable" className="rounded-sm data-[state=active]:bg-[#1A3668] data-[state=active]:text-white py-3">
            <Scale className="w-4 h-4 mr-2" />
            Acceptable Use
          </TabsTrigger>
        </TabsList>

        {/* Terms of Service */}
        <TabsContent value="terms">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#1A3668]" />
                Terms of Service
              </CardTitle>
              <p className="text-sm text-slate-500">Last updated: February 2026</p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="prose prose-slate max-w-none">
                  <h3 className="text-lg font-bold text-slate-900">1. Agreement to Terms</h3>
                  <p className="text-slate-600 mb-4">
                    By accessing or using BSO SiteTrack ("the Service"), you agree to be bound by these Terms of Service. 
                    If you disagree with any part of these terms, you may not access the Service.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">2. Description of Service</h3>
                  <p className="text-slate-600 mb-4">
                    BSO SiteTrack is a construction job costing and project management software platform designed to help 
                    contractors manage labor, materials, purchase orders, timesheets, and billing. The Service is 
                    provided "as is" and "as available" without warranties of any kind.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">3. User Accounts</h3>
                  <p className="text-slate-600 mb-4">
                    You are responsible for maintaining the confidentiality of your account credentials and for all 
                    activities that occur under your account. You must immediately notify us of any unauthorized use 
                    of your account or any other breach of security.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">4. User Responsibilities</h3>
                  <p className="text-slate-600 mb-2">You agree to:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and update your information as necessary</li>
                    <li>Use the Service only for lawful purposes</li>
                    <li>Not share your account credentials with unauthorized users</li>
                    <li>Comply with all applicable laws and regulations</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">5. Data Ownership</h3>
                  <p className="text-slate-600 mb-4">
                    You retain all ownership rights to the data you input into BSO SiteTrack. We claim no intellectual 
                    property rights over your data. You grant us permission to use your data solely to provide and 
                    improve the Service.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">6. Payment Terms</h3>
                  <p className="text-slate-600 mb-4">
                    Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable 
                    except as required by law. We reserve the right to modify pricing with 30 days notice.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">7. Limitation of Liability</h3>
                  <p className="text-slate-600 mb-4">
                    To the maximum extent permitted by law, BSO SiteTrack and its affiliates shall not be liable for any 
                    indirect, incidental, special, consequential, or punitive damages, including but not limited to 
                    loss of profits, data, or business opportunities.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">8. Termination</h3>
                  <p className="text-slate-600 mb-4">
                    We may terminate or suspend your account immediately, without prior notice, for any breach of 
                    these Terms. Upon termination, your right to use the Service will immediately cease. You may 
                    export your data within 30 days of termination.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">9. Changes to Terms</h3>
                  <p className="text-slate-600 mb-4">
                    We reserve the right to modify these terms at any time. We will provide notice of significant 
                    changes via email or through the Service. Continued use after changes constitutes acceptance 
                    of the modified terms.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">10. Contact Information</h3>
                  <p className="text-slate-600">
                    For questions about these Terms of Service, please contact us at legal@buildops.com or through 
                    the support channels provided in the application.
                  </p>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Privacy Policy */}
        <TabsContent value="privacy">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#1A3668]" />
                Privacy Policy
              </CardTitle>
              <p className="text-sm text-slate-500">Last updated: February 2026</p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="prose prose-slate max-w-none">
                  <h3 className="text-lg font-bold text-slate-900">1. Information We Collect</h3>
                  <p className="text-slate-600 mb-2">We collect information you provide directly:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Account information (name, email, company details)</li>
                    <li>Project data (jobs, timesheets, invoices, crew information)</li>
                    <li>Usage data (how you interact with our Service)</li>
                    <li>Communication data (support requests, feedback)</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">2. How We Use Your Information</h3>
                  <p className="text-slate-600 mb-2">We use your information to:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Provide and maintain the Service</li>
                    <li>Process transactions and send related information</li>
                    <li>Send technical notices and support messages</li>
                    <li>Respond to your comments and questions</li>
                    <li>Analyze usage patterns to improve the Service</li>
                    <li>Protect against fraudulent or unauthorized activity</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">3. Data Storage and Security</h3>
                  <p className="text-slate-600 mb-4">
                    Your data is stored on secure servers with encryption at rest and in transit. We implement 
                    industry-standard security measures including firewalls, access controls, and regular security 
                    audits. However, no method of transmission over the Internet is 100% secure.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">4. Data Sharing</h3>
                  <p className="text-slate-600 mb-2">We may share your information with:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Service providers who assist in operating the Service</li>
                    <li>Professional advisors (lawyers, accountants) as needed</li>
                    <li>Law enforcement when required by law</li>
                    <li>Successors in the event of a merger or acquisition</li>
                  </ul>
                  <p className="text-slate-600 mb-4">
                    We do not sell your personal information to third parties for marketing purposes.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">5. Your Rights</h3>
                  <p className="text-slate-600 mb-2">You have the right to:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Access and receive a copy of your personal data</li>
                    <li>Request correction of inaccurate data</li>
                    <li>Request deletion of your personal data</li>
                    <li>Object to processing of your personal data</li>
                    <li>Data portability (receive data in a structured format)</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">6. Data Retention</h3>
                  <p className="text-slate-600 mb-4">
                    We retain your data for as long as your account is active or as needed to provide the Service. 
                    After account termination, we retain data for up to 90 days for backup purposes, then securely 
                    delete it unless retention is required by law.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">7. Cookies and Tracking</h3>
                  <p className="text-slate-600 mb-4">
                    We use cookies and similar technologies to maintain your session, remember preferences, and 
                    analyze usage patterns. You can control cookie settings through your browser preferences.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">8. Children's Privacy</h3>
                  <p className="text-slate-600 mb-4">
                    BSO SiteTrack is not intended for users under 18 years of age. We do not knowingly collect personal 
                    information from children. If we learn we have collected such information, we will delete it 
                    promptly.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">9. Contact Us</h3>
                  <p className="text-slate-600">
                    For privacy-related inquiries, please contact our Data Protection Officer at privacy@buildops.com.
                  </p>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Acceptable Use Policy */}
        <TabsContent value="acceptable">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Scale className="w-6 h-6 text-[#1A3668]" />
                Acceptable Use Policy
              </CardTitle>
              <p className="text-sm text-slate-500">Last updated: February 2026</p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="prose prose-slate max-w-none">
                  <h3 className="text-lg font-bold text-slate-900">1. Permitted Use</h3>
                  <p className="text-slate-600 mb-4">
                    BSO SiteTrack is designed for legitimate construction business operations including job costing, 
                    project management, timekeeping, invoicing, and related activities. Use must comply with all 
                    applicable laws and regulations.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">2. Prohibited Activities</h3>
                  <p className="text-slate-600 mb-2">You may not use BSO SiteTrack to:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Violate any laws, regulations, or third-party rights</li>
                    <li>Submit false, misleading, or fraudulent information</li>
                    <li>Interfere with or disrupt the Service or servers</li>
                    <li>Attempt to gain unauthorized access to any systems</li>
                    <li>Upload malware, viruses, or other harmful code</li>
                    <li>Harvest or collect user information without consent</li>
                    <li>Use automated systems to access the Service without permission</li>
                    <li>Circumvent any security or access controls</li>
                    <li>Resell or redistribute the Service without authorization</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">3. Content Standards</h3>
                  <p className="text-slate-600 mb-2">All content uploaded must:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Be accurate and not misleading</li>
                    <li>Not infringe on intellectual property rights</li>
                    <li>Not contain defamatory or obscene material</li>
                    <li>Not promote illegal activities</li>
                    <li>Comply with applicable privacy laws</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">4. Account Security</h3>
                  <p className="text-slate-600 mb-2">You are responsible for:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Maintaining strong, unique passwords</li>
                    <li>Protecting your account credentials</li>
                    <li>Limiting access to authorized personnel only</li>
                    <li>Promptly reporting any security incidents</li>
                    <li>Ensuring user access levels match job responsibilities</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">5. Resource Limits</h3>
                  <p className="text-slate-600 mb-4">
                    To ensure fair use for all customers, we may impose reasonable limits on storage, API calls, 
                    and other resources. Enterprise plans may have higher limits. Excessive use that degrades 
                    performance for others may result in temporary restrictions.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">6. Compliance Requirements</h3>
                  <p className="text-slate-600 mb-4">
                    Users in regulated industries must ensure their use of BSO SiteTrack complies with applicable 
                    regulations including labor laws, prevailing wage requirements, and tax reporting obligations. 
                    BSO SiteTrack provides tools to assist compliance but does not guarantee regulatory compliance.
                  </p>

                  <h3 className="text-lg font-bold text-slate-900">7. Enforcement</h3>
                  <p className="text-slate-600 mb-2">Violations may result in:</p>
                  <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
                    <li>Warning and request to cease the activity</li>
                    <li>Temporary suspension of account access</li>
                    <li>Permanent termination of account</li>
                    <li>Legal action if warranted</li>
                  </ul>

                  <h3 className="text-lg font-bold text-slate-900">8. Reporting Violations</h3>
                  <p className="text-slate-600">
                    If you become aware of any violations of this policy, please report them immediately to 
                    abuse@buildops.com. We investigate all reports and take appropriate action.
                  </p>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
